import React, { useEffect, useMemo, useState } from "react";
import { FaRegStar, FaStar } from "react-icons/fa";
import {
  fetchProductRating,
  normalizeProductRating,
  readStoredAuth,
  setLocalProductRating,
  submitProductRating,
} from "../services/productRatingService";
import "../style/ProductRating.css";

function formatAverage(value) {
  if (!value) return "";
  return value.toLocaleString("it-IT", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
}

export default function ProductRating({
  productCode,
  productName,
  rating,
  compact = false,
  interactive = true,
}) {
  const [ratingState, setRatingState] = useState(() => normalizeProductRating(rating, productCode));
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setRatingState(normalizeProductRating(rating, productCode));
  }, [rating, productCode]);

  useEffect(() => {
    let mounted = true;
    const auth = readStoredAuth();
    fetchProductRating(productCode, auth?.access_token)
      .then((payload) => {
        if (!mounted) return;
        setRatingState(normalizeProductRating(payload.rating || payload, productCode));
      })
      .catch(() => {
        if (!mounted) return;
        setRatingState((current) => ({
          ...current,
          userRating: current.userRating || normalizeProductRating({}, productCode).userRating,
        }));
      });
    return () => {
      mounted = false;
    };
  }, [productCode]);

  const label = useMemo(() => {
    if (ratingState.count > 0 && ratingState.average > 0) {
      return `${formatAverage(ratingState.average)}/5 da ${ratingState.count} voti`;
    }
    if (ratingState.userRating > 0) {
      return `Il tuo voto: ${ratingState.userRating}/5`;
    }
    return "Ancora pochi voti";
  }, [ratingState]);

  const handleRate = async (value) => {
    if (!interactive || submitting) return;
    setSubmitting(true);
    setStatus("");
    setLocalProductRating(productCode, value);
    setRatingState((current) => ({ ...current, userRating: value }));

    const auth = readStoredAuth();
    if (!auth?.access_token) {
      setStatus("Accedi per sincronizzare il voto.");
      setSubmitting(false);
      return;
    }

    try {
      const payload = await submitProductRating(productCode, value, auth.access_token);
      setRatingState(normalizeProductRating(payload.rating || payload, productCode));
      setStatus("Voto sincronizzato.");
    } catch (_error) {
      setStatus("Voto salvato su questo browser.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`product-rating${compact ? " product-rating--compact" : ""}`}>
      <div
        className="product-rating__stars"
        role={interactive ? "radiogroup" : "img"}
        aria-label={`Valutazione ${productName || productCode}`}
      >
        {[1, 2, 3, 4, 5].map((value) => {
          const filled = value <= (ratingState.userRating || Math.round(ratingState.average || 0));
          const Icon = filled ? FaStar : FaRegStar;
          if (!interactive) {
            return <Icon key={value} aria-hidden="true" />;
          }
          return (
            <button
              key={value}
              type="button"
              className="product-rating__star-button"
              aria-label={`Vota ${value}/5`}
              aria-checked={ratingState.userRating === value}
              role="radio"
              disabled={submitting}
              onClick={() => handleRate(value)}
            >
              <Icon aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <span className="product-rating__label">{label}</span>
      {status ? <span className="product-rating__status">{status}</span> : null}
    </div>
  );
}

